import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, Typography } from '@mui/material';
import { AxiosError, AxiosResponse } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import { userLogin } from '../../../api/db/user';
import {
  gray100,
  primaryButtonBackgroundColor,
  primaryColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import { common } from '../../../modules/error';
import { userLoginSchema } from '../../../validation/user';
import type { UserLoginType } from '../../../validation/user/userType';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import PasswordTextField from '../../uiParts/PasswordTextfield';
import { AuthContext } from '../../../contexts/auth';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    height: '44px',
    fontSize: '16px',
    fontWeight: '500'
  },
  container: {
    '@media screen and (max-width:600px)': {
      height: `calc( 100vh - ${spHeaderHeight} - 24px)`,
      m: `${spHeaderHeight} auto 0`,
      pt: '24px',
      width: '90%'
    },
    height: `calc( 100vh - ${headerHeight} - 56px )`,
    m: `${headerHeight} auto 0`,
    pt: '56px',
    width: '560px'
  },
  link: {
    color: primaryColor,
    display: 'block',
    fontWeight: '500',
    m: '24px auto 0',
    textAlign: 'center'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

interface LoginResponse extends AxiosResponse {
  data: {
    message: string;
  };
}

const AuthLogin: React.FC = () => {
  const { isLoggedIn } = useContext(AuthContext);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/home');
    }
  }, [isLoggedIn]);

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const errorMessage = location.state?.error;

  useEffect(() => {
    if (errorMessage) {
      setErrorText(errorMessage);
      setOpen(true);
    }
  }, [errorMessage]);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<UserLoginType>({
    resolver: yupResolver(userLoginSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    return (
      watch('email') === undefined ||
      '' ||
      watch('password') === undefined ||
      '' ||
      Boolean(errors.email) ||
      Boolean(errors.password)
    );
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await userLogin(watch('email'), watch('password'));
      navigate('/home');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as LoginResponse;
        setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText(common);
      }
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="ログイン | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography sx={styles.title}>ログイン</Typography>
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.email)}
              formMb="24px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              requiredText="必須"
            />
          )}
          control={control}
          name="email"
        />

        <Controller
          render={({ field }) => (
            <PasswordTextField
              {...field}
              error={Boolean(errors.password)}
              formMb="40px"
              helperText={errors.password?.message}
              labelText="パスワード"
              requiredText="必須"
            />
          )}
          control={control}
          name="password"
        />

        <Button
          disabled={buttonDisabledFlag()}
          fullWidth
          onClick={() => {
            void (async () => {
              await handleSubmit(async () => await onSubmit())();
            })();
          }}
          sx={[primaryButtonBackgroundColor, styles.button]}
          variant="contained"
        >
          ログイン
        </Button>

        <Link
          color="inherit"
          href="/auth/password_reset"
          sx={[styles.link, { width: 'fit-content' }]}
        >
          パスワードを忘れた方
        </Link>

        <Link
          color="inherit"
          href="/auth/register"
          sx={[styles.link, { width: 'fit-content' }]}
        >
          新規登録の方はこちら
        </Link>
      </Typography>
      <CustomSnackbar
        autoHideDuration={6000}
        open={open}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default AuthLogin;
